// Pure helpers for the Independent Supporter home: which elections the switcher offers, which one
// is selected, and how a coverage level reads. Kept free of React so the rules are unit-tested.
import type {
	ISupporterCandidate,
	ISupporterCoverageLevel,
	ISupporterElection,
	ISupporterElectionSummary,
	ISupporterParty,
	ISupporterProfile,
} from '../../../interface/supporter';
import {
	type ICreateIncidentRequest,
	type IncidentCategory,
	type IncidentSeverity,
	MAX_INCIDENT_TITLE,
} from '../../../interface/vesr';

/**
 * The elections the switcher lists: the profile's linked elections and nothing else.
 *
 * <p>When the token names elections, the list is also narrowed to those. api.core's summary route
 * and api.vesr's create both check the token's `election` claims, so an election linked after this
 * token was minted would only answer 403 until the next refresh (at most fifteen minutes away).
 * A token with no election claims at all (one minted before they existed) narrows nothing.</p>
 */
export const supporterElectionOptions = (profile: ISupporterProfile | null, claimIds: number[]): ISupporterElection[] => {
	const seen = new Set<number>();
	return (profile?.elections ?? []).filter((e) => {
		if (!e || !(e.electionId > 0) || seen.has(e.electionId)) return false;
		if (claimIds.length && !claimIds.includes(e.electionId)) return false;
		seen.add(e.electionId);
		return true;
	});
};

/** The remembered election when it is still offered, otherwise the first. Null when none is offered. */
export const pickElection = (options: ISupporterElection[], remembered: number | null | undefined): number | null => {
	if (remembered && options.some((o) => o.electionId === remembered)) return remembered;
	return options[0]?.electionId ?? null;
};

export const electionLabel = (election: ISupporterElection | null | undefined): string =>
	election?.name?.trim() || (election ? `Election ${election.electionId}` : '');

export const candidateName = (candidate: ISupporterCandidate | null | undefined): string =>
	[candidate?.firstName, candidate?.lastName]
		.map((p) => p?.trim())
		.filter(Boolean)
		.join(' ');

/** "PDP" when there is a short name, else the full name. */
export const partyLabel = (party: ISupporterParty | null | undefined): string =>
	party?.shortName?.trim() || party?.name?.trim() || '';

/** Per-user key: two accounts on one phone must not inherit each other's choice. */
export const electionStorageKey = (userId: string | number | undefined) => `ielect-supporter-election:${userId ?? ''}`;

export type CoverageView =
	/** Nothing on record for this level: no total to measure against. Never shown as 0%. */
	{ kind: 'empty' } | { kind: 'measured'; uploaded: number; total: number; percent: number };

const count = (n: unknown): number => (typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0);

/**
 * How one level reads. A total of zero means api.core has no units of that level on record for
 * the election (or the party's ledger is not provisioned yet) — "0 of 0" is not a measurement, so
 * it is an empty state, never 0%. Uploaded is clamped to the total: a ledger that briefly reports
 * more uploads than units must not draw a bar past 100%.
 */
export const coverageView = (level: ISupporterCoverageLevel | null | undefined): CoverageView => {
	const total = count(level?.totalCount);
	if (!total) return { kind: 'empty' };
	const uploaded = Math.min(count(level?.uploadedCount), total);
	return { kind: 'measured', uploaded, total, percent: Math.round((uploaded / total) * 100) };
};

export interface ICoverageRow {
	key: 'pollingUnit' | 'ward' | 'lga' | 'state';
	label: string;
	unit: string;
	view: CoverageView;
}

export const coverageRows = (summary: ISupporterElectionSummary | null): ICoverageRow[] => [
	{ key: 'pollingUnit', label: 'Polling units', unit: 'polling units', view: coverageView(summary?.pollingUnit) },
	{ key: 'ward', label: 'Wards', unit: 'wards', view: coverageView(summary?.ward) },
	{ key: 'lga', label: 'LGAs', unit: 'LGAs', view: coverageView(summary?.lga) },
	{ key: 'state', label: 'States', unit: 'states', view: coverageView(summary?.state) },
];

/** True when no level has anything on record: the whole card is one empty state. */
export const coverageIsEmpty = (rows: ICoverageRow[]) => rows.every((r) => r.view.kind === 'empty');

export interface ISupporterReportInput {
	title: string;
	note?: string;
	category: IncidentCategory;
	severity: IncidentSeverity;
	/** Only when the supporter chose to share it. */
	coords?: { latitude: number; longitude: number } | null;
}

/**
 * The VESR create body for a supporter's report. `electionId` is ALWAYS the selected election, as
 * the core id in string form: api.vesr defaults it only when the account follows exactly one
 * election and answers 400 "Choose which of your elections…" otherwise, and it 403s an election
 * the token does not name. No place codes — a supporter has no posting; location is optional.
 */
export const buildSupporterIncidentRequest = (electionId: number, input: ISupporterReportInput): ICreateIncidentRequest => {
	const body: ICreateIncidentRequest = {
		title: input.title.trim().slice(0, MAX_INCIDENT_TITLE),
		description: input.note?.trim() || undefined,
		category: input.category,
		severity: input.severity,
		electionId: String(electionId),
	};
	if (input.coords && Number.isFinite(input.coords.latitude) && Number.isFinite(input.coords.longitude)) {
		body.latitude = input.coords.latitude;
		body.longitude = input.coords.longitude;
	}
	return body;
};
